import { useContext, useMemo } from 'react';
import { SFMenuOption } from 'sfui';
import { Customer, CustomerContext } from 'ui-smartforce-settings';
import { IntegrationReportsContext } from '../../../../../../Context/IntegrationReportsContext';
import { CADRMSReportValueType } from '../../../../../../Models/CadReportValue';
import { ReportValue } from '../../../../../../Models/ReportValue';
import { getCADLocation } from '../../Helpers/incidentNumber';

interface ReportOptionData {
  date: string | undefined;
  fullAddress: string | undefined;
  disposition: string | undefined;
  subjectsCount: number | undefined;
}

/**
 * Extracts display data for a report option in the case number dropdown.
 */
export const getReportOptionData = (
  incidentNumber: string,
  reports: ReportValue[]
): ReportOptionData => {
  const report = reports.find(
    (r) => r.general_information.incident_number === incidentNumber
  );

  if (!report || !report.cad_rms) {
    return {
      date: undefined,
      fullAddress: undefined,
      disposition: undefined,
      subjectsCount: undefined
    };
  }

  return {
    date: report.cad_rms?.cad.date_time.received,
    fullAddress: getCADLocation(report.cad_rms as CADRMSReportValueType),
    disposition: report.cad_rms?.cad.disposition,
    subjectsCount:
      report.cad_rms?.subjects && report.cad_rms.subjects.length > 0
        ? report.cad_rms.subjects.length
        : 0
  };
};

export interface UseCaseNumberFieldResult {
  getReportDataForOption: (option: SFMenuOption) => ReportOptionData;
  timezone: string;
}

/**
 * Provides data needed to render case number dropdown options.
 * UI structure remains in the component; this hook owns context + derived data.
 */
export const useCaseNumberField = (): UseCaseNumberFieldResult => {
  const { integrationReports } = useContext(IntegrationReportsContext);
  const customer = useContext(CustomerContext).customer as Customer;

  const getReportDataForOption = useMemo(
    () => (option: SFMenuOption) =>
      getReportOptionData(option.value, integrationReports),
    [integrationReports]
  );

  return {
    getReportDataForOption,
    timezone: customer.timezone
  };
};
